import React, { useState } from 'react';
import { Play, ExternalLink, Trash2 } from 'lucide-react';
import { getRedGifsId, getRedGifsWatchUrl, getRedGifsThumbnail, isRedGifsUrl } from '@/lib/utils';
import { VideoPlayer } from './VideoPlayer';
import type { CoupleMessage, Clip } from '@/types';
import { RedGifsImage } from '@/components/RedGifsImage';

interface ChatMessageProps {
  message: CoupleMessage;
  isOwn: boolean;
  clips?: Clip[];
  onDelete?: (messageId: string) => void;
}

// Common emoji shortcuts
const QUICK_EMOJIS = ['❤️', '🔥', '😍', '👍', '😈', '💦', '🍆', '🍑', '😘', '🥵'];

// Detect URLs in text and split into parts
const parseMessageWithUrls = (text: string): Array<{ type: 'text' | 'url'; content: string }> => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts: Array<{ type: 'text' | 'url'; content: string }> = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before URL
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    // Add URL
    parts.push({ type: 'url', content: match[0] });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
};

// Extract clip ID from internal /clip/ URLs
const getInternalClipId = (url: string): string | null => {
  const match = url.match(/\/clip\/([a-zA-Z0-9_-]+)/);
  return match?.[1] ?? null;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, isOwn, clips = [], onDelete }) => {
  const [showEmbed, setShowEmbed] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Check if message has a shared clip
  const sharedClip = message.clipId ? clips.find(c => c.id === message.clipId) : null;

  // Parse message for URLs
  const messageParts = parseMessageWithUrls(message.message);
  const redGifsUrls = messageParts
    .filter(p => p.type === 'url' && isRedGifsUrl(p.content))
    .map(p => p.content);

  // Find internal clip URLs (like /clip/swallow-1) and look up the clips
  const internalClipUrls = messageParts
    .filter(p => p.type === 'url' && getInternalClipId(p.content))
    .map(p => {
      const clipId = getInternalClipId(p.content);
      const clip = clipId ? clips.find(c => c.id === clipId) : null;
      return { url: p.content, clipId, clip };
    })
    .filter(item => item.clip);

  // System messages (notifications) get special styling
  if (message.fromType === 'system') {
    return (
      <div className="flex justify-center my-3">
        <div className="max-w-[90%] bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl px-4 py-3 text-center">
          <p className="text-sm text-white whitespace-pre-wrap">{message.message}</p>
          <p className="text-xs text-purple-400 mt-2">
            {message.fromName} • {new Date(message.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </p>
          {/* Shared clip card for system messages */}
          {sharedClip && (
            <a
              href={`/clip/${sharedClip.id}`}
              className="block mt-3 rounded-xl overflow-hidden border border-purple-500/30 hover:border-lime-500/50 transition-colors"
            >
              <div className="bg-zinc-800 px-3 py-2">
                <p className="text-white text-sm font-medium">{sharedClip.title}</p>
              </div>
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] space-y-2`}>
        {/* Main message bubble */}
        <div
          className={`rounded-2xl px-4 py-2 ${
            isOwn
              ? 'bg-blue-500/30 text-white rounded-br-md'
              : 'bg-zinc-700 text-white rounded-bl-md'
          }`}
        >
          {/* Message text with URL detection */}
          <p className="text-sm whitespace-pre-wrap">
            {messageParts.map((part, i) => {
              if (part.type === 'url') {
                // RedGifs URLs - toggle embed
                if (isRedGifsUrl(part.content)) {
                  return (
                    <button
                      key={i}
                      onClick={() => setShowEmbed(!showEmbed)}
                      className="text-lime-400 hover:underline inline-flex items-center gap-1"
                    >
                      <Play className="w-3 h-3" />
                      {getRedGifsId(part.content) || 'RedGifs Video'}
                    </button>
                  );
                }
                // Internal clip URLs - show as clip link (embed shown below)
                const internalClipId = getInternalClipId(part.content);
                if (internalClipId) {
                  const clip = clips.find(c => c.id === internalClipId);
                  return (
                    <a
                      key={i}
                      href={`/clip/${internalClipId}`}
                      className="text-pink-400 hover:underline inline-flex items-center gap-1"
                    >
                      <Play className="w-3 h-3" />
                      {clip?.title || internalClipId}
                    </a>
                  );
                }
                // Other URLs - external link
                return (
                  <a
                    key={i}
                    href={part.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    {part.content.length > 40 ? part.content.slice(0, 40) + '...' : part.content}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                );
              }
              return <span key={i}>{part.content}</span>;
            })}
          </p>

          {/* Timestamp and Delete */}
          <div className={`flex items-center justify-between gap-2 mt-1`}>
            <p className={`text-xs ${isOwn ? 'text-blue-300' : 'text-zinc-400'}`}>
              {message.fromName} • {new Date(message.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
            {isOwn && onDelete && (
              showDeleteConfirm ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onDelete(message.id)}
                    className="text-red-400 text-xs hover:text-red-300"
                  >
                    Delete
                  </button>
                  <span className="text-zinc-500 text-xs">/</span>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="text-zinc-400 text-xs hover:text-zinc-300"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-zinc-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )
            )}
          </div>
        </div>

        {/* Shared clip card - links to clip detail page */}
        {sharedClip && (
          <a
            href={`/clip/${sharedClip.id}`}
            className={`block rounded-xl overflow-hidden border ${isOwn ? 'border-blue-500/30' : 'border-zinc-600'} hover:border-lime-500/50 transition-colors`}
          >
            <div className="aspect-video bg-black relative">
              {(() => {
                const thumb = getRedGifsThumbnail(sharedClip.link);
                return thumb ? (
                  <RedGifsImage url={sharedClip.link} alt={sharedClip.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500">
                    <Play className="w-8 h-8" />
                  </div>
                );
              })()}
            </div>
            <div className="bg-zinc-800 px-3 py-2">
              <p className="text-white text-sm font-medium truncate">{sharedClip.title}</p>
              {sharedClip.description && (
                <p className="text-zinc-400 text-xs truncate">{sharedClip.description}</p>
              )}
            </div>
          </a>
        )}

        {/* Internal clip URL embeds (from pasted /clip/ URLs) */}
        {internalClipUrls.length > 0 && (
          <div className="space-y-2">
            {internalClipUrls.map(({ clipId, clip }) => clip && (
              <a
                key={clipId}
                href={`/clip/${clip.id}`}
                className={`block rounded-xl overflow-hidden border ${isOwn ? 'border-blue-500/30' : 'border-zinc-600'} hover:border-pink-500/50 transition-colors`}
              >
                <div className="aspect-video bg-black relative">
                  {(() => {
                    const thumb = getRedGifsThumbnail(clip.link);
                    return thumb ? (
                      <RedGifsImage url={clip.link} alt={clip.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-500">
                        <Play className="w-8 h-8" />
                      </div>
                    );
                  })()}
                </div>
                <div className="bg-zinc-800 px-3 py-2">
                  <p className="text-white text-sm font-medium truncate">{clip.title}</p>
                  {clip.description && (
                    <p className="text-zinc-400 text-xs truncate">{clip.description}</p>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

        {/* RedGifs URL embeds (when clicked) */}
        {showEmbed && redGifsUrls.length > 0 && (
          <div className="space-y-2">
            {redGifsUrls.map((url, i) => {
              const watchUrl = getRedGifsWatchUrl(url);
              return (
                <div key={i} className="rounded-xl overflow-hidden border border-zinc-600">
                  <VideoPlayer url={url} />
                  <div className="bg-zinc-800 px-3 py-2 flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">RedGifs</span>
                    {watchUrl && (
                      <a
                        href={watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lime-400 text-xs hover:underline flex items-center gap-1"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Emoji picker component
interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export const QuickEmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect }) => {
  return (
    <div className="flex gap-1 p-2 bg-zinc-800 rounded-xl border border-zinc-700">
      {QUICK_EMOJIS.map(emoji => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className="w-8 h-8 flex items-center justify-center text-lg hover:bg-zinc-700 rounded-lg transition-colors"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

export default ChatMessage;
